import api from '@/api';
import type {
    PromosAdvisoriesDetailsResponse,
    PromosAdvisoriesItem,
    PromosAdvisoriesResponse,
} from '@/types/promosAdvisories';
import { ApiFactory } from './base/ApiFactory';

/**
 * Service class for interacting with the Promo Advisories API endpoints.
 * Handles promo and advisory listing and details operations.
 */
class PromoAdvisoriesService extends ApiFactory<PromosAdvisoriesItem> {
    /**
     * Creates an instance of PromoAdvisoriesService.
     */
    constructor() {
        super('/promo-advisory', api);
    }

    /**
     * Gets the list of promos and advisories with pagination.
     * @param page - Optional page number (default: 1)
     * @param perPage - Optional items per page (default: 10)
     * @returns Promise with promos/advisories response including pagination data
     */
    async getPromosAdvisoriesList(
        page: number = 1,
        perPage: number = 10,
    ): Promise<PromosAdvisoriesResponse> {
        return this.get<PromosAdvisoriesResponse>(`${this.resource}/list`, {
            page,
            per_page: perPage,
        });
    }

    /**
     * Gets the details of a specific promo or advisory.
     * @param promosAdvisoriesId - The ID of the promo/advisory to retrieve
     * @returns Promise with detailed promo/advisory data
     */
    async getPromosAdvisoriesDetails(
        promosAdvisoriesId: number,
    ): Promise<PromosAdvisoriesDetailsResponse> {
        return this.get<PromosAdvisoriesDetailsResponse>(
            `${this.resource}/${promosAdvisoriesId}`,
        );
    }
}

// Export a singleton instance
export const promoAdvisoriesService = new PromoAdvisoriesService();
export default PromoAdvisoriesService;
